/**
 * Horizontal segment-card track for the Mix Plan Review (§6.5).
 * Supports:
 *   - select item (calls onSelect)
 *   - drag-and-drop reorder (HTML5 drag API, no external lib)
 *   - delete via trash button on hover
 */
import { useState } from 'react'
import { Trash2 } from 'lucide-react'

import { SegmentCard } from './SegmentCard'
import type { DramaSegment, TimelineItem } from '@/types/drama'
import { Button } from '@/components/ui/button'

interface Props {
  timeline: TimelineItem[]
  segmentMap: Map<string, DramaSegment>
  activeOrder?: number
  onSelect: (order: number) => void
  onReorder: (fromIdx: number, toIdx: number) => void
  onDelete: (order: number) => void
}

export function TimelineTrack({
  timeline,
  segmentMap,
  activeOrder,
  onSelect,
  onReorder,
  onDelete,
}: Props) {
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)

  if (timeline.length === 0) {
    return (
      <div className="border-2 border-dashed rounded-md p-6 text-center text-sm text-muted-foreground">
        时间线为空。从「Replace / Insert」面板拖入一个片段。
      </div>
    )
  }

  return (
    <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1">
      {timeline.map((item, idx) => {
        const seg = segmentMap.get(item.segment_code)
        const start = item.source_start_offset || 0
        const end =
          item.source_end_offset ??
          (seg ? seg.end_seconds - seg.start_seconds : 0)
        const duration = Math.max(0, end - start)
        const isActive = activeOrder === item.order
        const isDropTarget = dragOver === idx && dragFrom !== idx
        return (
          <div key={item.order} className="relative">
            {isDropTarget && (
              <div className="absolute -left-1.5 top-0 bottom-0 w-1 bg-spectrum-magenta rounded-full" />
            )}
            <div className="relative">
              <SegmentCard
                segmentCode={item.segment_code}
                episodeNumber={seg?.episode_number}
                purpose={item.purpose}
                caption={item.caption}
                durationLabel={`${duration.toFixed(1)}s`}
                active={isActive}
                draggable
                onClick={() => onSelect(item.order)}
                onDragStart={(e) => {
                  setDragFrom(idx)
                  e.dataTransfer.effectAllowed = 'move'
                  e.dataTransfer.setData('text/plain', String(idx))
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragOver(idx)
                  e.dataTransfer.dropEffect = 'move'
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  const fromIdx = Number(e.dataTransfer.getData('text/plain'))
                  setDragFrom(null)
                  setDragOver(null)
                  if (!Number.isNaN(fromIdx) && fromIdx !== idx) {
                    onReorder(fromIdx, idx)
                  }
                }}
              />
              <Button
                size="sm"
                variant="ghost"
                className="absolute -top-2 -right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 bg-background border shadow-sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete(item.order)
                }}
                title="从时间线移除"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
